'use client';

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ILaunchEvent } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE, resolveI18nText } from '@/sanity/globals/I18n';
import { resolveLink } from '@/sanity/globals/CTA';
import { extractAffCode, withAffCode } from '@/lib/launchEvent';
import { useLaunchEventLive } from '@/hooks/useLaunchEventLive';
import {
  ArrowDots,
  EventCardBackground,
  pad,
  useTimeLeft,
} from '@/components/global/eventWidget/EventWidgetParts';

/**
 * Corner widget promoting the quarterly launch event.
 *
 * Built 1:1 from Figma node 7637:16277 (IQ Capital | New UI 2026). Sizes are
 * the Figma px divided by 8, which is this project's rem scale (see
 * app/globals.scss), so the card renders at its design size on a 1920 viewport.
 *
 * Scheduled by the Launch Event Widget singleton (sanity/globals/LaunchEvent.ts)
 * and mounted on the homepage and every affiliate page. On affiliate pages it
 * forwards the page's partner code as `?aff=` so the sign-up automation
 * attributes the lead exactly like the checkout CTAs on the same page.
 *
 * Dismissal is per browser tab (sessionStorage): closing it should not sink
 * the campaign for the rest of launch week, but it should stay closed while
 * the visitor keeps browsing.
 */

/** Figma gives each unit its own fixed width (36/35/31/31px → /8 rem). */
/**
 * Figma gives each unit its own fixed width, and the two frames use different
 * ones (mobile 27.89/27.115/24.016px, desktop 36/35/31px → /8 rem). A CSS
 * variable per breakpoint is the only way to keep those per-unit widths while
 * still switching them at `lg`, since an inline style has no media query.
 */
function CountdownUnit({
  value,
  label,
  width,
  mobileWidth,
}: {
  value: number;
  label: string;
  width: string;
  mobileWidth: string;
}) {
  return (
    <div
      className="flex h-[4.648rem] w-[var(--unit-w-mobile)] shrink-0 flex-col items-center gap-[0.2905rem] lg:h-[6rem] lg:w-[var(--unit-w)] lg:gap-[0.375rem]"
      style={
        {
          '--unit-w-mobile': mobileWidth,
          '--unit-w': width,
        } as CSSProperties
      }
    >
      <p className="mb-0 whitespace-nowrap font-geist text-[2.5rem] font-normal leading-[1.1079] tracking-[-0.0252rem] text-white lg:text-[3.25rem] lg:leading-[1.1] lg:tracking-[-0.0325rem]">
        {pad(value)}
      </p>
      <p className="mb-0 whitespace-nowrap font-geist text-[1.162rem] font-normal uppercase leading-[1.1666] tracking-[0.0271rem] text-white/70 lg:text-[1.5rem] lg:tracking-[0.035rem]">
        {label}
      </p>
    </div>
  );
}

function Colon() {
  return (
    <p className="mb-0 shrink-0 font-geist text-[1.5495rem] leading-[1.7] text-white/70 lg:text-[2rem]">
      :
    </p>
  );
}

interface LaunchEventWidgetProps {
  data?: ILaunchEvent | null;
  language?: string;
  /**
   * The affiliate page's checkout CTA. Its `aff` code is copied onto the
   * sign-up link; omit on pages that have no partner attribution of their own
   * (the visitor's own `?aff=`/UTM params still come through `resolveLink`).
   */
  affCtaUrl?: string;
  className?: string;
}

export default function LaunchEventWidget({
  data,
  language,
  affCtaUrl,
  className,
}: LaunchEventWidgetProps) {
  const { config, audience, live } = useLaunchEventLive(data, language);
  const [dismissed, setDismissed] = useState(false);
  const [href, setHref] = useState('');

  // One key per campaign window, so the next campaign is not hidden by a
  // dismissal of the previous one.
  const storageKey = `launchEventDismissed:${config?.startDate ?? 'default'}`;

  // The audience's own countdown target: the German event on German pages,
  // the international one everywhere else.
  const target = useMemo(() => {
    const raw = audience?.countdownEndDate;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isFinite(date.getTime()) ? date : null;
  }, [audience?.countdownEndDate]);

  const start = useMemo(() => {
    if (!config?.startDate) return null;
    const date = new Date(config.startDate);
    return Number.isFinite(date.getTime()) ? date : null;
  }, [config?.startDate]);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(storageKey) === '1');
    } catch {
      // Private mode / blocked storage: showing the widget is the safe default.
    }
  }, [storageKey]);

  // resolveLink reads the visitor's URL and localStorage, so it only runs in
  // the browser — same pattern as BirthdayPopUp.
  useEffect(() => {
    const path = audience?.ctaPath || '/launch-event';
    const resolved = resolveLink(path, language || DEFAULT_LANGUAGE);
    setHref(withAffCode(resolved, extractAffCode(affCtaUrl)));
  }, [audience?.ctaPath, language, affCtaUrl]);

  const timeLeft = useTimeLeft(target);

  const onDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // Dismissal then lasts for this page view only — acceptable.
    }
  };

  if (!live || !config || dismissed) return null;

  const eyebrow = resolveI18nText(config.eyebrow, config.eyebrowI18n, language);
  const headline = resolveI18nText(config.headline, config.headlineI18n, language);
  const ctaLabel = resolveI18nText(config.ctaLabel, config.ctaLabelI18n, language);
  const daysLabel = resolveI18nText(config.daysLabel ?? 'DAYS', config.daysLabelI18n, language);
  const hoursLabel = resolveI18nText(config.hoursLabel ?? 'HRS', config.hoursLabelI18n, language);
  const minutesLabel = resolveI18nText(config.minutesLabel ?? 'MIN', config.minutesLabelI18n, language);
  const secondsLabel = resolveI18nText(config.secondsLabel ?? 'SEC', config.secondsLabelI18n, language);

  // Share of the campaign window already elapsed — the thin accent bar under
  // the headline. Without both bounds it stays at the Figma's 94/242 sliver.
  const progress =
    start && target && target.getTime() > start.getTime()
      ? Math.min(
          100,
          Math.max(
            0,
            ((Date.now() - start.getTime()) / (target.getTime() - start.getTime())) * 100,
          ),
        )
      : 38.84;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 110, damping: 20, delay: 0.6 }}
        className={cn(
          'fixed bottom-[2rem] left-[2rem] right-[2rem] z-50 lg:bottom-[3rem] lg:left-[3rem] lg:right-auto',
          className,
        )}
      >
        {/* Close button. On mobile (8354:252) it floats clear of the card —
            right-aligned with its edge, 3px above the top, on a solid #11332a
            fill — so it sits OUTSIDE the card, which clips its own overflow.
            On desktop (7637:16277) it sits inside the top-right corner on the
            translucent fill. Both are positioned against this wrapper, whose
            box matches the card in either layout. */}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="absolute -top-[4.125rem] right-0 z-10 flex h-[3.75rem] w-[3.75rem] shrink-0 items-center justify-center rounded-full border border-solid border-white/[0.24] bg-[#11332a] backdrop-blur-[4px] transition-colors hover:bg-[#1a4a3c] lg:right-[2.75rem] lg:top-[2.75rem] lg:bg-white/[0.06] lg:hover:bg-white/[0.14]"
        >
          <svg
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden
            className="h-[1.5rem] w-[1.5rem]"
          >
            <path
              d="M3 3L9 9M9 3L3 9"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Card: 380x125 on mobile (8191:16430), 471x175 on desktop
            (7637:16277). The desktop fixed width is replaced by a min-width so
            the longer German headline grows the card instead of being clipped;
            at the English copy the two are identical. */}
        <div className="relative w-full overflow-hidden rounded-[0.5rem] border border-solid border-white/[0.14] px-[2rem] pb-[2rem] pt-[2.5rem] shadow-[0px_1.75rem_1.975rem_0px_rgba(0,0,0,0.08)] lg:w-auto lg:min-w-[58.875rem] lg:p-[2.75rem]">
          <EventCardBackground />

          {/* Mobile (8191:16430): headline and countdown share one row, the
              CTA runs full width underneath. Desktop (7637:16277): headline
              over countdown on the left, CTA bottom-right. */}
          <div className="relative flex flex-col gap-[1.625rem] lg:flex-row lg:items-end lg:justify-between lg:gap-[2rem]">
            <div className="flex w-full items-center justify-between gap-[1.5rem] lg:w-[30.25rem] lg:flex-col lg:items-start lg:gap-[2rem]">
              {/* Figma gives this block `flex: 1 0 0` with `min-width: 0`, so
                  it yields to the fixed-width countdown instead of running
                  under it — which the longer German headline otherwise does. */}
              <div className="flex min-w-0 flex-1 flex-col items-start gap-[0.875rem] lg:flex-none lg:gap-[0.375rem]">
                <p className="mb-0 whitespace-nowrap font-blacker text-[2.32rem] italic leading-[0.9236] tracking-[-0.0595rem] text-[#ffda74] lg:text-[3rem] lg:leading-[1.035] lg:tracking-[-0.08625rem]">
                  {eyebrow}
                </p>
                <p className="mb-0 font-blacker text-[2.32rem] leading-[0.9236] tracking-[-0.0595rem] text-white lg:whitespace-nowrap lg:text-[3rem] lg:leading-[1.035] lg:tracking-[-0.08625rem]">
                  {headline}
                </p>
              </div>

              {/* Progress bar + countdown: 46px tall and 155px wide on mobile,
                  59px tall across the column on desktop. */}
              <div className="flex h-[5.75rem] w-[19.375rem] shrink-0 flex-col justify-between lg:h-[7.375rem] lg:w-full">
                <div className="h-[0.1936rem] w-full overflow-hidden rounded-[0.1936rem] bg-[rgba(152,152,152,0.4)] lg:h-[0.25rem] lg:rounded-[0.25rem]">
                  <div
                    className="h-full rounded-[0.1936rem] bg-[#5ae4bf] lg:rounded-[0.25rem]"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {timeLeft && (
                  <div className="flex w-full items-start justify-between">
                    <CountdownUnit value={timeLeft.days} label={daysLabel} width="4.5rem" mobileWidth="3.486rem" />
                    <Colon />
                    <CountdownUnit value={timeLeft.hours} label={hoursLabel} width="4.375rem" mobileWidth="3.389rem" />
                    <Colon />
                    <CountdownUnit value={timeLeft.minutes} label={minutesLabel} width="3.875rem" mobileWidth="3.002rem" />
                    <Colon />
                    <CountdownUnit value={timeLeft.seconds} label={secondsLabel} width="3.875rem" mobileWidth="3.002rem" />
                  </div>
                )}
              </div>
            </div>

            {/* CTA — full width under the content on mobile, bottom-right of
                the card on desktop. */}
            <div className="w-full lg:w-auto">
              {href && (
                <Link
                  href={href}
                  className="flex w-full items-center justify-center gap-[0.5rem] rounded-[0.375rem] bg-[#ffda74] px-[1.25rem] py-[1rem] transition-colors hover:bg-[#ffe9a6] lg:w-auto lg:p-[1.25rem]"
                >
                  <span className="whitespace-nowrap font-geist text-[1.5rem] font-normal leading-[1.1667] tracking-[-0.015rem] text-black lg:text-[1.75rem] lg:leading-[1] lg:tracking-[-0.0175rem]">
                    {ctaLabel}
                  </span>
                  <ArrowDots />
                </Link>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
